import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useInvestmentPhilosophy } from '../hooks/useInvestmentPhilosophy';
import { QUESTIONS } from '../config/investmentArchetypes';
import { IPTopBar } from '../components/investment-philosophy/IPTopBar';
import { IPProgressTracker } from '../components/investment-philosophy/IPProgressTracker';
import { IPIntroScreen } from '../components/investment-philosophy/IPIntroScreen';
import { IPQuestionCard } from '../components/investment-philosophy/IPQuestionCard';
import { IPResultsScreen } from '../components/investment-philosophy/IPResultsScreen';

export const InvestmentPhilosophyPage: React.FC = () => {
  const { state, totalQuestions, startQuiz, answer, goBack, restart } =
    useInvestmentPhilosophy();

  const hasInProgress = state.responses.some((r) => r != null);
  const currentQuestion = QUESTIONS[state.currentQuestionIndex];

  const useScrollLayout = state.phase === 'results';

  return (
    <>
      <Helmet>
        <title>Investment Philosophy Quiz | Wallstreetle</title>
        <meta
          name="description"
          content="Find your investing archetype across 9 legendary Wall Street investors, from Buffett to Cathie Wood."
        />
      </Helmet>

      <div
        className={`flex flex-col bg-background ${
          useScrollLayout ? 'min-h-[100dvh]' : 'h-[100dvh] overflow-hidden'
        }`}
      >
        <IPTopBar />

        <div
          className="flex-1 flex flex-col min-h-0 max-w-[480px] w-full mx-auto px-3 sm:px-4 pb-2"
          style={{ paddingTop: 'calc(3rem + env(safe-area-inset-top))' }}
        >
          {state.phase === 'intro' && (
            <IPIntroScreen
              hasInProgress={hasInProgress}
              onStart={startQuiz}
              onStartOver={restart}
            />
          )}

          {state.phase === 'active' && currentQuestion && (
            <>
              <div className="pt-3">
                <IPProgressTracker
                  responses={state.responses}
                  currentIndex={state.currentQuestionIndex}
                  total={totalQuestions}
                />
              </div>
              <IPQuestionCard
                question={currentQuestion}
                index={state.currentQuestionIndex}
                total={totalQuestions}
                selected={state.responses[state.currentQuestionIndex]}
                canGoBack={state.currentQuestionIndex > 0}
                onAnswer={(response) => answer(state.currentQuestionIndex, response)}
                onBack={goBack}
              />
            </>
          )}

          {state.phase === 'computing' && (
            <div className="flex-1 flex items-center justify-center">
              <div
                className="text-sm font-medium animate-pulse"
                style={{ color: 'hsl(var(--muted-foreground))' }}
              >
                Calculating your archetype…
              </div>
            </div>
          )}

          {state.phase === 'results' && state.matches && state.profile && (
            <div className="pt-4">
              <IPResultsScreen
                matches={state.matches}
                profile={state.profile}
                onRestart={restart}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
